import { EditOutlined } from '@ant-design/icons';
import { ColumnType, ColumnsType } from 'antd/lib/table/interface';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { RingDuration } from 'business/production-and-performance/pages/ring-table/components/ring-table/ring-duration';
import { useFormatMetricPoint } from 'business/production-and-performance/pages/ring-table/hooks/use-format-metric-point';
import { parseRingTimeToLocalizedString } from 'business/production-and-performance/pages/ring-table/services/ring-time';
import {
  RingTableColumnKeys,
  RingTableRow,
} from 'business/production-and-performance/pages/ring-table/types';
import { useMediaType } from 'technical/media/hooks';
import Button from 'ui/button';

const CELL_WIDTH = 135;
const MOBILE_SMALL_CELL_WIDTH = 90;

export const useRingTableColumns = (
  onEditionClick: (id: string | null) => void,
): ColumnsType<RingTableRow> => {
  const { t } = useTranslation();
  const { formatMetricPoint } = useFormatMetricPoint();

  const { isMobile } = useMediaType();

  const { currentConstructionSite } = useAppContext();
  const {
    isDataManager,
    modules: {
      PROD_PERF: { isConstructionSiteManager },
    },
  } = usePermissions();

  const edit = (record: RingTableRow) => {
    onEditionClick(record.id);
  };

  const commonColumnProps: Partial<ColumnType<RingTableRow>> = {
    align: 'center',
    width: CELL_WIDTH,
    sorter: true,
  };

  const mobileSmallColumnProps: Pick<ColumnType<RingTableRow>, 'width'> = {
    width: MOBILE_SMALL_CELL_WIDTH,
  };

  const columns: ColumnsType<RingTableRow> = [
    {
      title: t('productionAndPerformance.ring.table.ringNumber'),
      dataIndex: RingTableColumnKeys.RingNumber,
      key: RingTableColumnKeys.RingNumber,
      defaultSortOrder: 'descend',
      fixed: true,
      ...commonColumnProps,
      ...(isMobile ? mobileSmallColumnProps : {}),
    },
    {
      title: t('productionAndPerformance.ring.table.keyPosition'),
      dataIndex: RingTableColumnKeys.KeyPosition,
      key: RingTableColumnKeys.KeyPosition,
      render: (_, { keyPosition }) => {
        if (!keyPosition) {
          return '-';
        }
        return (currentConstructionSite?.keyPositionPrefix || '') + keyPosition;
      },
      ...commonColumnProps,
      ...(isMobile ? mobileSmallColumnProps : {}),
    },
    {
      title: t('productionAndPerformance.ring.table.length'),
      dataIndex: RingTableColumnKeys.Length,
      key: RingTableColumnKeys.Length,
      render: (_, { length }) => {
        if (length === 1 && currentConstructionSite?.ringLengthCode1) {
          return currentConstructionSite.ringLengthCode1;
        }
        if (length === 0 && currentConstructionSite?.ringLengthCode0) {
          return currentConstructionSite.ringLengthCode0;
        }
        return '-';
      },
      ...commonColumnProps,
    },
    {
      title: t('productionAndPerformance.ring.table.buildEndMetricPoint'),
      dataIndex: RingTableColumnKeys.BuildEndMetricPoint,
      key: RingTableColumnKeys.BuildEndMetricPoint,
      render: (_, { buildEndMetricPoint }) =>
        formatMetricPoint(buildEndMetricPoint),
      ...commonColumnProps,
    },
    {
      title: t('productionAndPerformance.ring.table.excavation'),
      key: 'excavation',
      children: [
        {
          title: t('productionAndPerformance.ring.table.start'),
          dataIndex: RingTableColumnKeys.ExcavationStart,
          key: RingTableColumnKeys.ExcavationStart,
          render: (_, { excavationStart }) =>
            parseRingTimeToLocalizedString(excavationStart),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.end'),
          dataIndex: RingTableColumnKeys.ExcavationEnd,
          key: RingTableColumnKeys.ExcavationEnd,
          render: (_, { excavationEnd }) =>
            parseRingTimeToLocalizedString(excavationEnd),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.totalDuration'),
          dataIndex: RingTableColumnKeys.TotalExcavationDuration,
          key: RingTableColumnKeys.TotalExcavationDuration,
          render: (_, { totalExcavationDuration }) => (
            <RingDuration rawRingDuration={totalExcavationDuration} />
          ),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.realDuration'),
          dataIndex: RingTableColumnKeys.RealExcavationDuration,
          key: RingTableColumnKeys.RealExcavationDuration,
          render: (_, { realExcavationDuration }) => (
            <RingDuration rawRingDuration={realExcavationDuration} />
          ),
          ...commonColumnProps,
        },
      ],
    },
    {
      title: t('productionAndPerformance.ring.table.build'),
      key: 'build',
      children: [
        {
          title: t('productionAndPerformance.ring.table.start'),
          dataIndex: RingTableColumnKeys.BuildStart,
          key: RingTableColumnKeys.BuildStart,
          render: (_, { buildStart }) =>
            parseRingTimeToLocalizedString(buildStart),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.end'),
          dataIndex: RingTableColumnKeys.BuildEnd,
          key: RingTableColumnKeys.BuildEnd,
          render: (_, { buildEnd }) => parseRingTimeToLocalizedString(buildEnd),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.totalDuration'),
          dataIndex: RingTableColumnKeys.TotalBuildDuration,
          key: RingTableColumnKeys.TotalBuildDuration,
          render: (_, { totalBuildDuration }) => (
            <RingDuration rawRingDuration={totalBuildDuration} />
          ),
          ...commonColumnProps,
        },
        {
          title: t('productionAndPerformance.ring.table.realDuration'),
          dataIndex: RingTableColumnKeys.RealBuildDuration,
          key: RingTableColumnKeys.RealBuildDuration,
          render: (_, { realBuildDuration }) => (
            <RingDuration rawRingDuration={realBuildDuration} />
          ),
          ...commonColumnProps,
        },
      ],
    },
  ];

  if (!isMobile && (isConstructionSiteManager || isDataManager)) {
    columns.push({
      title: t('productionAndPerformance.ring.table.action.title'),
      key: 'editionColumn',
      render: (_, record) => {
        return (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => edit(record)}
          />
        );
      },
      align: 'center',
      width: '5%',
    } satisfies ColumnType<RingTableRow>);
  }

  return columns;
};
